<script setup lang="ts">
import { computed } from 'vue';
import { VBtn } from 'vuetify/components';

export type ButtonVariant = 'primary' | 'secondary' | 'special' | 'text' | 'ghost';
export type ButtonSize = 'x-small' | 'small' | 'default' | 'large' | 'x-large';
export type IconPosition = 'start' | 'end';

export interface ButtonProps {
  variant: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  icon?: string;
  iconPosition?: IconPosition;
}

const {
  disabled = false,
  loading = false,
  icon = undefined,
  iconPosition = 'end',
  label = undefined,
  size = 'default',
  variant,
} = defineProps<ButtonProps>();

// Map our custom variant name to Vuetify's VBtn variant prop values
const vBtnUnderlyingVariant = computed(() => {
  switch (variant) {
    case 'primary':
      return 'elevated';
    case 'secondary':
      return 'outlined';
    case 'special':
      return 'elevated';
    case 'text':
      return 'text';
    case 'ghost':
      return 'text';
    default:
      return 'elevated';
  }
});

const variantClass = computed(() => {
  switch (variant) {
    case 'primary':
      return 'primary-v-btn-overwrite';
    case 'secondary':
      return 'secondary-v-btn-overwrite';
    case 'special':
      return 'special-v-btn-overwrite';
    case 'text':
      return 'text-v-btn-overwrite';
    case 'ghost':
      return 'ghost-v-btn-overwrite';
    default:
      return '';
  }
});

const isIconOnly = computed(() => {
  return icon !== undefined && label === undefined;
});

const activePrependIcon = computed(() => {
  return icon && iconPosition === 'start' ? icon : undefined;
});

const activeAppendIcon = computed(() => {
  return icon && iconPosition === 'end' ? icon : undefined;
});
</script>

<template>
  <VBtn
    v-bind="$attrs"
    :variant="vBtnUnderlyingVariant"
    :size="size !== 'default' ? size : undefined"
    :disabled="disabled"
    :loading="loading"
    :prependIcon="activePrependIcon"
    :appendIcon="activeAppendIcon"
    :class="[variantClass, { 'icon-only': isIconOnly, 'v-btn--icon': isIconOnly }]"
  >
    <template v-if="$slots.prepend" #prepend>
      <slot name="prepend" />
    </template>
    <template v-if="$slots.append" #append>
      <slot name="append" />
    </template>
    <span v-if="label">{{ label }}</span>
    <slot v-else />
  </VBtn>
</template>

<style scoped lang="scss">
.icon-only :deep(.v-btn__prepend),
.icon-only :deep(.v-btn__append) {
  margin-inline: 0 !important;
}
</style>
